import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useFlag } from 'utils/launchdarkly';
import { useGetRestaurantAvailabilityFn, useIsMobileOrderingAvailable } from 'utils/restaurant';
import { PICKUP_SERVICE_MODES, isCatering } from 'utils/service-mode';

/**
 * A store that never supports mobile ordering and one that can't take an order right now are the
 * same dead end to a guest, so FHSG-825 merged them behind this single label from the design.
 */
const MOBILE_ORDERING_NOT_AVAILABLE = 'Mobile Ordering Not Available';

interface UseErrorMessageOptions {
  restaurant: IRestaurantNode;
}

export const useMessage = ({ restaurant }: UseErrorMessageOptions) => {
  const { formatMessage } = useIntl();
  const { serviceModeStatus } = useServiceModeStatus(restaurant);
  const getRestaurantAvailability = useGetRestaurantAvailabilityFn();
  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);
  const { serviceMode } = useServiceModeContext();

  const isRestaurantAvailable = getRestaurantAvailability(restaurant);
  const isRestaurantOpen = PICKUP_SERVICE_MODES.some(mode => serviceModeStatus[mode].available);
  const enableFutureOrdering = useFlag('enable-future-ordering');

  const isNotAvailable = useCallback(
    (mode: ServiceMode): boolean => {
      const status = serviceModeStatus[mode];

      if (!status) {
        return true;
      }

      return !status.available && !status.disabled;
    },
    [serviceModeStatus]
  );

  return useMemo(() => {
    const isCateringNotAvailableAndEnabled =
      isCatering(serviceMode) && isNotAvailable(ServiceMode.CATERING_PICKUP);
    if (restaurant.hasMobileOrdering && !isRestaurantOpen && !enableFutureOrdering) {
      return formatMessage({ id: 'locationClosed' });
    }

    if (!restaurant.hasMobileOrdering || !isMobileOrderingAvailable || !isRestaurantAvailable) {
      return MOBILE_ORDERING_NOT_AVAILABLE;
    }

    if (isCateringNotAvailableAndEnabled) {
      return formatMessage({ id: 'storeDoesNotHaveCateringOrders' });
    }

    return;
  }, [
    serviceMode,
    isNotAvailable,
    restaurant.hasMobileOrdering,
    isRestaurantOpen,
    enableFutureOrdering,
    isMobileOrderingAvailable,
    isRestaurantAvailable,
    formatMessage,
  ]);
};
